import {
  formatDate,
  getNextAvailableDate,
  isDateSameOrAfter,
  isDateSameOrBefore,
  getAddedBusinessDaysDate,
  getSubtractedBusinessDaysDate,
  isSameDay
} from './dates'
import { calendarTypes, GENESIS_IHAT, timeSlots, TIME_SLOT_IDS, DATE_FORMATS, CALENDAR_TABS, API_SEARCH_TERMS, MESSAGE_KEYWORDS } from '../constants/scheduling'
import { CALENDAR_BASE_URL, API_BASE_URL } from '../constants/urls'
import { orderSubmitActions } from '../constants/order'

const { orderSubmit: ORDER_SUBMIT } = orderSubmitActions

export const isPrewireWithAMSlots = calendar => 
  calendar.calendarType?.toLowerCase() === calendarTypes.prewire && 
  calendar.calendarDates?.some(date => 
    date.availableTimeSlots?.some(slot => 
      slot.id === TIME_SLOT_IDS.AM || slot.time === TIME_SLOT_IDS.AM
    )
  )

export const isPrewireWithoutAMSlots = calendar => 
  calendar.calendarType?.toLowerCase() === calendarTypes.prewire && 
  calendar.calendarDates?.every(date => 
    !date.availableTimeSlots?.some(slot => 
      slot.id === TIME_SLOT_IDS.AM || slot.time === TIME_SLOT_IDS.AM
    )
  )

export const isPrewireWithEmptySlots = calendar => 
  calendar.calendarType?.toLowerCase() === calendarTypes.prewire && 
  calendar.calendarDates?.every(date => 
    !date.availableTimeSlots || date.availableTimeSlots.length === 0
  )

export function setApiPropertiesFromCallbackList(
  response,
  optionType = CALENDAR_TABS.FULL_AUTOMATION
) {
  if (!response?.nextApiToCall?.length) {
    return null
  }

  const searchTerm =
    optionType === CALENDAR_TABS.PARTIAL_AUTOMATION
      ? API_SEARCH_TERMS.SUBMIT
      : API_SEARCH_TERMS.VALIDATE
  const selectedApi = response.nextApiToCall.find(eachList =>
    eachList.apiName.toLowerCase().includes(searchTerm)
  )

  if (selectedApi) {
    response.apiName = selectedApi.apiName
    response.apiToCall = selectedApi.apiToCall
    return selectedApi
  }

  return null
}

export function parseSchedulingCalendars({
  calendars,
  isEscalationAvailable,
  nextApiToCall,
  messages = [],
  ...data
}) {
  const { prewire, install, escalation } = calendarTypes
  // Extract apiToCall and apiName from nextApiToCall array (use first item as default)
  const hasMultipleApis = nextApiToCall?.length === 2
  const [defaultApi] = nextApiToCall || []
  const apiSource = hasMultipleApis ? data : (defaultApi || {})
  const apiToCall = apiSource.apiToCall || ''
  const apiName = apiSource.apiName || ''
  const apiBase = apiName === ORDER_SUBMIT ? API_BASE_URL : CALENDAR_BASE_URL
  const installMessageCodes = messages.reduce(
    (codes, msg) => (msg.code ? [...codes, msg.code] : codes),
    []
  )
  const genesisMsg = messages.find(message => message.code === GENESIS_IHAT)

  const technicalIssueMsg = messages.find(message => {
    const desc = message.description?.toLowerCase() || ''
    return (
      desc.includes(MESSAGE_KEYWORDS.TECHNICAL_ISSUE) &&
      desc.includes(MESSAGE_KEYWORDS.TIMESLOTS)
    )
  })

  const hasDoiCalendar = calendars?.some(
    cal => cal.calendarType.toLowerCase() === calendarTypes.doi
  )
  const hasCdddCalendar = calendars?.some(
    cal => cal.calendarType.toLowerCase() === calendarTypes.cddd
  )

  const isValidDoiScenario = hasDoiCalendar && !hasCdddCalendar

  return {
    ...data,
    apiUrl: apiBase + apiToCall,
    apiAction: apiName,
    installMessageCodes,
    genesisIhatMessage: genesisMsg?.description || '',
    technicalIssueMessage: technicalIssueMsg?.description || '',
    hasCalendars: !!calendars?.length,
    ...(calendars || []).reduce((cals, cal) => {
      const [firstDate] = cal.calendarDates || []
      
      const isFirstDateToday = firstDate?.date && isSameDay(firstDate.date)
      
      if (isFirstDateToday && firstDate?.availableTimeSlots) {
        firstDate.availableTimeSlots = firstDate.availableTimeSlots.filter(
          slot => slot.id !== TIME_SLOT_IDS.AM && slot.id !== TIME_SLOT_IDS.PM
        )
      }
      
      const [firstSlot] = firstDate?.availableTimeSlots || []
 
      const anySlot = firstDate?.availableTimeSlots?.find(
        slot => slot.id === TIME_SLOT_IDS.ANY
      )
      const defaultSlot = isFirstDateToday && anySlot ? anySlot : firstSlot
      
      const selected = isValidDoiScenario
        ? {
            date: null,
            timeSlot: null
          }
        : {
            date: firstDate?.date || null,
            timeSlot: defaultSlot || null
          }

      return {
        ...cals,
        [cal.calendarType.toLowerCase() === calendarTypes.prewire
          ? prewire
          : install]: {
          ...cal,
          selected
        }
      }
    }, {}),
    ...(isEscalationAvailable && {
      [escalation]: {
        selected: {
          date: formatDate(
            getNextAvailableDate(new Date(), data.disabledDates),
            DATE_FORMATS.ISO_DATE
          ),
          timeSlot: timeSlots[2]
        }
      }
    })
  }
}

export function getFilteredCalendar(calendar) {
  const {
    prewire,
    install,
    preWireMinGapBeforeInstallCalendar: minDaysBeforeInstall,
    installationMessage
  } = calendar

  if (!(prewire && install) || installationMessage) {
    return calendar
  }

  const isSoftScenario = 
  install.calendarType?.toLowerCase() !== calendarTypes.doi

  if (!isSoftScenario) {
    if (!prewire.selected?.date) {
      return calendar
    }
    const prewireHasTimeslots = prewire.calendarDates?.some(date => 
      date.availableTimeSlots?.length > 0
    )
    if (prewireHasTimeslots) {
      return calendar
    }
  }

  const minDays = minDaysBeforeInstall || 0
  const { date: lastInstallDate } = install.calendarDates[
    install.calendarDates.length - 1
  ]
  const allCalendarDates = install.calendarDates.map(d => d.date)

  const minInstallDate = getAddedBusinessDaysDate(
    prewire.selected.date,
    minDays,
    allCalendarDates
  )
  const maxPreInstallDate = getSubtractedBusinessDaysDate(
    lastInstallDate,
    minDays,
    allCalendarDates
  )

  const defaultInstallDateTime = install.calendarDates.find(({ date }) =>
    isDateSameOrAfter(date, minInstallDate)
  )
  const filteredInstallDates = install.calendarDates.filter(({ date }) =>
    isDateSameOrAfter(date, defaultInstallDateTime.date)
  )
  const filteredPreWireDates = prewire.calendarDates.filter(({ date }) =>
    isDateSameOrBefore(date, maxPreInstallDate)
  )

  const [firstInstallDate] = filteredInstallDates
  const [firstInstallSlot] = firstInstallDate?.availableTimeSlots || []
  const [firstPrewireDate] = filteredPreWireDates
  const [firstPrewireSlot] = firstPrewireDate?.availableTimeSlots || []

  const isFirstInstallToday = firstInstallDate?.date 
  && isSameDay(firstInstallDate.date)
  const installAnySlot = firstInstallDate?.availableTimeSlots?.find(
    slot => slot.id === TIME_SLOT_IDS.ANY
  )
  const defaultInstallSlot = isFirstInstallToday && installAnySlot 
    ? installAnySlot 
    : firstInstallSlot

  const isFirstPrewireToday = firstPrewireDate?.date 
  && isSameDay(firstPrewireDate.date)
  const prewireAnySlot = firstPrewireDate?.availableTimeSlots?.find(
    slot => slot.id === TIME_SLOT_IDS.ANY
  )
  const defaultPrewireSlot = isFirstPrewireToday && prewireAnySlot 
    ? prewireAnySlot 
    : firstPrewireSlot

  return {
    ...calendar,
    allCalendarDates,
    install: {
      ...install,
      calendarDates: filteredInstallDates,
      selected: isSoftScenario 
        ? {
            date: firstInstallDate?.date || null,
            timeSlot: defaultInstallSlot || null
          }
        : {
            date: install.selected.date || null,
            timeSlot: install.selected.timeSlot || null
          }
    },
    prewire: {
      ...prewire,
      calendarDates: filteredPreWireDates,
      selected: isSoftScenario 
        ? {
            date: firstPrewireDate?.date || null,
            timeSlot: defaultPrewireSlot || null
          }
        : {
            date: prewire.selected.date || null,
            timeSlot: prewire.selected.timeSlot || null
          }
    }
  }
}

export function getUpdatedCalendarDate(state, payload) {
  const { calendar } = state.scheduling
  const {
    install,
    prewire,
    allCalendarDates,
    preWireMinGapBeforeInstallCalendar: minDaysBeforeInstall
  } = calendar

  if (!(prewire && install)) {
    return false
  }

  const { calendarType, selectedDate } = payload
  const isPreWire = calendarType === calendarTypes.prewire
  
  const hasDoiCalendar = 
  install.calendarType?.toLowerCase() === calendarTypes.doi
  
  if (isPreWire && hasDoiCalendar && install.selected?.date) {
    return {
      calType: calendarTypes.install,
      updatedDate: {
        date: null,
        timeSlot: null
      },
      clearInstallHighlights: true
    }
  }

  if (isCalendarValid(calendar, payload)) {
    return false
  }

  const currentTimeSlot = isPreWire
    ? install.selected.timeSlot
    : prewire.selected.timeSlot

  const nextValidDate = isPreWire
    ? getAddedBusinessDaysDate(
        selectedDate.date,
        minDaysBeforeInstall,
        allCalendarDates
      )
    : getSubtractedBusinessDaysDate(
        selectedDate.date,
        minDaysBeforeInstall,
        allCalendarDates
      )

  const foundDate = isPreWire
    ? findAvailableDate(install.calendarDates, nextValidDate, isDateSameOrAfter)
    : findAvailableDate(
        [...prewire.calendarDates].reverse(),
        nextValidDate,
        isDateSameOrBefore
      )

  if (!foundDate) {
    return false
  }

  const { date: updatedCalDate, availableTimeSlots } = foundDate
  const updatedTimeSlot =
    availableTimeSlots?.find(slot => slot.id === currentTimeSlot?.id) ||
    availableTimeSlots?.[0]

  const shouldUpdateDate =
    (payload.selectedDate?.timeSlot?.id ?? null) === TIME_SLOT_IDS.AM ||
    (payload.selectedDate?.timeSlot?.id ?? null) === TIME_SLOT_IDS.PM ||
    (payload.selectedDate?.timeSlot?.id ?? null) === TIME_SLOT_IDS.ANY

  return {
    calType: isPreWire ? calendarTypes.install : calendarTypes.prewire,
    updatedDate: {
      date: shouldUpdateDate ? updatedCalDate : null,
      timeSlot: updatedTimeSlot
    }
  }
}

function isCalendarValid(calendar, payload) {
  const { calendarType, selectedDate } = payload
  const {
    install,
    prewire,
    allCalendarDates,
    preWireMinGapBeforeInstallCalendar: minDaysBeforeInstall
  } = calendar
  const isPreWire = calendarType === calendarTypes.prewire

  if (isPreWire) {
    const date = getAddedBusinessDaysDate(
      selectedDate.date,
      minDaysBeforeInstall,
      allCalendarDates
    )
    return isDateSameOrBefore(date, install.selected.date)
  }

  const nextValidDate = getSubtractedBusinessDaysDate(
    selectedDate.date,
    minDaysBeforeInstall,
    allCalendarDates
  )
  return isDateSameOrAfter(nextValidDate, prewire.selected.date)
}

const findAvailableDate = (calDates, dt, fn) =>
  calDates.find(calDate => fn(calDate.date, dt))

export function shouldShowCalendarOptions(validateInstallResponse) {
  return validateInstallResponse?.nextApiToCall?.length === 2
}

export function filterCalendarsByOption(calendars, selectedOption) {
  if (!calendars || !selectedOption) {
    return calendars
  }

  if (selectedOption === CALENDAR_TABS.FULL_AUTOMATION) {
    return calendars.filter(cal => 
      cal.calendarType?.toLowerCase() === calendarTypes.doi
    )
  }

  if (selectedOption === CALENDAR_TABS.PARTIAL_AUTOMATION) {
    return calendars.filter(cal => 
      cal.calendarType?.toLowerCase() === calendarTypes.cddd
    )
  }

  return calendars
}

export function getDefaultCalendarOption(calendars) {
  if (!calendars?.length) {
    return CALENDAR_TABS.FULL_AUTOMATION
  }

  const hasDoiCalendar = calendars.some(
    cal => cal.calendarType?.toLowerCase() === calendarTypes.doi
  )

  return hasDoiCalendar
    ? CALENDAR_TABS.FULL_AUTOMATION
    : CALENDAR_TABS.PARTIAL_AUTOMATION
}

export function addDaysToDate(dateStr, days) {
  const date = new Date(dateStr)
  date.setDate(date.getDate() + days)
  return date.toISOString().split('T')[0]
}

export function getDoiMinDate(prewireSelectedDate, minGap) {
  if (!prewireSelectedDate) {
    return null
  }
  const gap = minGap || 0
  return addDaysToDate(prewireSelectedDate, gap + 1)
}

export function getPrewireMaxDate(lastInstallDate, minGap) {
  if (!lastInstallDate) {
    return null
  }
  
  const gap = minGap || 0
  return addDaysToDate(lastInstallDate, -gap)
}
